/**
 * @file CreditsWidget.js
 * @description Wallet/credits UI + inline Stripe card flyout.
 */
import React, { useEffect, useState, useRef, Fragment } from 'react';
import * as creditService from '../services/creditService';
import { readInsufficientFlag, clearInsufficientFlag } from '../services/creditService';

import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';

import './CreditsWidget.css';

const PACK_CREDITS = {
  credits_100: 100,
  credits_250: 250,
  credits_500: 500,
  credits_1000: 1000,
};

// Stripe publishable key (test or live, from env)
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

/**
 * Inner flyout component that lives inside <Elements>.
 * Handles:
 *  - Listing saved Stripe cards (deduped by backend)
 *  - Selecting a default card for instant purchase
 *  - Adding a new card via SetupIntent + CardElement
 */
function PaymentMethodFlyout({ open, onClose, onSaved, onOpenPortal }) {
  const stripe = useStripe();
  const elements = useElements();

  // SetupIntent + new card state
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedSummary, setSavedSummary] = useState(null);

  // Existing methods + default selection
  const [methods, setMethods] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [methodsError, setMethodsError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [changingDefault, setChangingDefault] = useState(false);
  const [isMockMode, setIsMockMode] = useState(false);

  // Load existing cards when flyout opens
  useEffect(() => {
    let cancelled = false;

    async function loadMethods() {
      if (!open) {
        setMethods([]);
        setMethodsLoading(false);
        setMethodsError('');
        setSelectedId(null);
        setIsMockMode(false);
        return;
      }

      setMethodsLoading(true);
      setMethodsError('');
      setIsMockMode(false);

      try {
        const res = await creditService.listPaymentMethods();
        if (cancelled) return;

        const list = Array.isArray(res?.methods) ? res.methods : [];
        setMethods(list);

        const defaultItem = list.find((m) => m.isDefault);
        if (defaultItem) {
          setSelectedId(defaultItem.id);
        } else if (list.length > 0) {
          setSelectedId(list[0].id);
        }
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.body?.message ||
            e?.body?.error ||
            e?.message ||
            'Failed to load saved payment methods.';

          // If backend says MOCK_PAYMENTS is required, treat this as "on the house"
          if (msg && msg.includes('MOCK_PAYMENTS')) {
            setIsMockMode(true);
            setMethodsError('');
            setMethods([]); // no cards to show
          } else {
            setMethodsError(msg);
          }
        }
      } finally {
        if (!cancelled) setMethodsLoading(false);
      }
    }

    loadMethods();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // When flyout opens, fetch a SetupIntent from backend for adding new card
  useEffect(() => {
    let cancelled = false;

    async function boot() {
      if (!open) {
        setClientSecret(null);
        setError('');
        setSavedSummary(null);
        return;
      }

      setError('');
      setClientSecret(null);
      setSavedSummary(null);
      setLoadingIntent(true);
      try {
        const { clientSecret } = await creditService.createSetupIntent();
        if (!cancelled) {
          setClientSecret(clientSecret || null);
        }
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.body?.message ||
            e?.body?.error ||
            e?.message ||
            'Failed to initialize card setup.';

          if (msg && msg.includes('MOCK_PAYMENTS')) {
            // Mock mode: no SetupIntent, no card entry, just "credits are on the house"
            setIsMockMode(true);
            setError('');
            setClientSecret(null);
          } else {
            setError(msg);
          }
        }
      } finally {
        if (!cancelled) setLoadingIntent(false);
      }
    }

    boot();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const handleSelect = async (pmId) => {
    if (!pmId || pmId === selectedId) return;
    setSelectedId(pmId);
    setChangingDefault(true);
    setMethodsError('');
    try {
      const res = await creditService.setDefaultPaymentMethod(pmId);
      const summary = {
        brand: res?.brand || 'card',
        last4: res?.last4 || '',
        expMonth: res?.expMonth || null,
        expYear: res?.expYear || null,
      };
      setSavedSummary(summary);
      if (onSaved) onSaved(summary);
    } catch (e) {
      // Revert selection only visually; backend default didn't change
      setMethodsError(
        e?.body?.message ||
        e?.body?.error ||
        e?.message ||
        'Unable to set default payment method.'
      );
    } finally {
      setChangingDefault(false);
    }
  };

  const handleAddCard = async (evt) => {
    evt.preventDefault();
    setError('');

    if (!stripe || !elements) {
      setError('Payment system is still loading…');
      return;
    }
    if (!clientSecret) {
      setError('Card setup is not ready yet. Please wait a moment.');
      return;
    }

    const card = elements.getElement(CardElement);
    if (!card) {
      setError('Card input is not ready.');
      return;
    }

    setSaving(true);
    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card,
        },
      });

      if (result.error) {
        setError(result.error.message || 'Card confirmation failed.');
        setSaving(false);
        return;
      }

      const setupIntent = result.setupIntent;
      const pmId = setupIntent.payment_method;
      if (!pmId) {
        setError('No payment method returned by Stripe.');
        setSaving(false);
        return;
      }

      // Attach and set as default on backend
      const res = await creditService.savePaymentMethod(pmId);
      const summary = {
        brand: res.brand,
        last4: res.last4,
        expMonth: res.expMonth,
        expYear: res.expYear,
      };
      setSavedSummary(summary);
      if (onSaved) onSaved(summary);

      // Refresh methods so the new card appears and is marked default
      try {
        const methodsRes = await creditService.listPaymentMethods();
        const list = Array.isArray(methodsRes?.methods) ? methodsRes.methods : [];
        setMethods(list);
        const def =
          list.find((m) => m.isDefault) || list.find((m) => m.id === pmId);
        if (def) setSelectedId(def.id);
      } catch {
        // non-fatal
      }

      // Auto-close after successful add
      if (onClose) onClose();
    } catch (e) {
      setError(
        e?.body?.message ||
        e?.body?.error ||
        e?.message ||
        'Failed to save payment method.'
      );
    } finally {
      setSaving(false);
    }
  };

  const renderStatus = () => {
    if (loadingIntent) return 'Initializing secure card form…';
    if (savedSummary) {
      const brand = (savedSummary.brand || 'Card').toUpperCase();
      return `${brand} ending in ${savedSummary.last4} is set as your default card.`;
    }
    return 'Your card details are encrypted and processed by Stripe.';
  };

  const handleOpenPortalClick = async () => {
    if (!onOpenPortal) return;
    try {
      await onOpenPortal();
    } catch (e) {
      // onOpenPortal already handles alerting; swallow here.
    }
  };

  return (
    <div className="wallet-flyout-backdrop">
      <div className="wallet-flyout-panel">
        <div className="wallet-flyout-header">
          <span>Payment Methods</span>
          <button
            type="button"
            className="wallet-flyout-close"
            onClick={onClose}
            aria-label="Close payment methods"
          >
            ×
          </button>
        </div>

        <div className="wallet-flyout-body">
          {isMockMode ? (
            <div className="wallet-flyout-info">
              Credits are on the house in this environment. No card is required. Just keep buying credits.
            </div>
          ) : (methodsError || error) && (
            <div className="wallet-flyout-error">
              {methodsError || error}
            </div>
          )}

          <p className="wallet-flyout-note">
            {renderStatus()}
          </p>

          {/* Saved cards list */}
          <div className="wallet-flyout-section">
            <h4 className="wallet-flyout-subtitle">Saved cards</h4>
            {methodsLoading ? (
              <div className="wallet-flyout-note">
                Loading saved cards…
              </div>
            ) : methods.length === 0 ? (
              <div className="wallet-flyout-note">
                You don&apos;t have any cards on file yet.
              </div>
            ) : (
              <ul className="wallet-card-list">
                {methods.map((m) => {
                  const brand = (m.brand || 'Card').toUpperCase();
                  const mm =
                    m.expMonth != null
                      ? String(m.expMonth).padStart(2, '0')
                      : null;
                  const yy =
                    m.expYear != null
                      ? String(m.expYear).slice(-2)
                      : null;

                  return (
                    <li key={m.id} className="wallet-card-item">
                      <label className="wallet-card-label">
                        <input
                          type="radio"
                          name="wallet-default-card"
                          value={m.id}
                          checked={selectedId === m.id}
                          onChange={() => handleSelect(m.id)}
                          disabled={changingDefault}
                        />
                        <span className="wallet-card-text">
                          <span className="wallet-card-main">
                            {brand} •••• {m.last4}
                            {mm && yy && (
                              <span className="wallet-card-sub-inline">
                                {' '}
                                · exp {mm}/{yy}
                              </span>
                            )}
                            {m.isDefault && (
                              <span className="wallet-card-badge">
                                Default
                              </span>
                            )}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Add new card */}
          <div className="wallet-flyout-section">
            <h4 className="wallet-flyout-subtitle">Add a new card</h4>
            <form onSubmit={handleAddCard}>
              <div className="wallet-card-element-wrap">
                <CardElement
                  options={{
                    disableLink: true, // Disable Stripe "Link" autofill button
                    style: {
                      base: {
                        fontSize: '14px',
                        color: '#e5e5e5',
                        '::placeholder': {
                          color: '#737373',
                        },
                      },
                      invalid: {
                        color: '#fca5a5',
                      },
                    },
                  }}
                />
              </div>

              <div className="wallet-flyout-actions">
                <button
                  type="submit"
                  className="wallet-flyout-save-btn"
                  disabled={
                    saving ||
                    loadingIntent ||
                    !stripe ||
                    !elements ||
                    !clientSecret
                  }
                >
                  {saving ? 'Saving…' : 'Add card & set as default'}
                </button>

                {onOpenPortal && (
                  <button
                    type="button"
                    className="wallet-flyout-portal-btn"
                    onClick={handleOpenPortalClick}
                  >
                    Open Stripe billing portal
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Flyout showing cost of credits (USD) and cost of features (Credits).
 */
function PricingFlyout({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="wallet-flyout-backdrop">
      <div className="wallet-flyout-panel wallet-pricing-panel">
        <div className="wallet-flyout-header">
          <span>Pricing & Costs</span>
          <button
            type="button"
            className="wallet-flyout-close"
            onClick={onClose}
            aria-label="Close pricing info"
          >
            ×
          </button>
        </div>

        <div className="wallet-flyout-body">
          <div className="wallet-pricing-section">
            <h4 className="wallet-flyout-subtitle">Buy Credits</h4>
            <div className="wallet-pricing-table">
              <div className="wallet-pricing-row">
                <span>100 Credits</span>
                <span className="wallet-pricing-cost">$10.00</span>
              </div>
              <div className="wallet-pricing-row">
                <span>250 Credits</span>
                <span className="wallet-pricing-cost">$25.00</span>
              </div>
              <div className="wallet-pricing-row">
                <span>500 Credits</span>
                <span className="wallet-pricing-cost">$50.00</span>
              </div>
              <div className="wallet-pricing-row">
                <span>1000 Credits</span>
                <span className="wallet-pricing-cost">$100.00</span>
              </div>
            </div>
            <div className="wallet-pricing-note">
              1 credit ≈ $0.10
            </div>
          </div>

          <div className="wallet-flyout-section">
            <h4 className="wallet-flyout-subtitle">Spend Credits</h4>
            <div className="wallet-pricing-table">
              <div className="wallet-pricing-row">
                <span>Image</span>
                <span className="wallet-pricing-cost">12 credits</span>
              </div>
              <div className="wallet-pricing-row">
                <span>Video</span>
                <span className="wallet-pricing-cost">4 credits / sec</span>
              </div>
              <div className="wallet-pricing-row">
                <span>Music</span>
                <span className="wallet-pricing-cost">10 credits / generation</span>
              </div>
              <div className="wallet-pricing-row">
                <span>Speech</span>
                <span className="wallet-pricing-cost">0.6 credits / sec</span>
              </div>
              <div className="wallet-pricing-row">
                <span>Screenplay</span>
                <span className="wallet-pricing-cost">80 credits / job</span>
              </div>
              <div className="wallet-pricing-row">
                <span>Character Bible</span>
                <span className="wallet-pricing-cost">12 credits / character</span>
              </div>
              <div className="wallet-pricing-row">
                <span>Scene</span>
                <span className="wallet-pricing-cost">12 credits / scene</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreditsWidget({ className = '' }) {
  const [balance, setBalance] = useState(null);
  const [hasMadePurchase, setHasMadePurchase] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  // Custom amount state (initialized to 250 credits = $25)
  const [customAmount, setCustomAmount] = useState('250');
  const [packCode, setPackCode] = useState('credits_250');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [showInsufficient, setShowInsufficient] = useState(false);
  const [insufficientMeta, setInsufficientMeta] = useState(null);

  const [showBillingFlyout, setShowBillingFlyout] = useState(false);
  const [showPricingInfo, setShowPricingInfo] = useState(false);
  const [defaultCard, setDefaultCard] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(true);

  // Close dropdown when clicking outside
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // If empty when closing, reset to default
        if (!customAmount || customAmount === '') {
          setCustomAmount('250');
          setPackCode('credits_250');
        }
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef, customAmount]);

  async function refresh() {
    try {
      setIsLoading(true);
      const res = await creditService.getBalance(); // { userId, balance, cardBrand?, cardLast4?, hasMadePurchase? ... }
      setBalance(typeof res?.balance === 'number' ? res.balance : 0);
      setHasMadePurchase(!!res?.hasMadePurchase);

      const enabled = res && typeof res.stripeEnabled === 'boolean' ? res.stripeEnabled : true;
      setStripeEnabled(enabled);

      // Hydrate defaultCard from backend if we have a saved card summary
      if (enabled && res && (res.cardBrand || res.cardLast4)) {
        setDefaultCard({
          brand: res.cardBrand || 'card',
          last4: res.cardLast4 || '',
          expMonth: res.cardExpMonth || null,
          expYear: res.cardExpYear || null,
        });
      } else {
        // No card on file -> clear any previous default card in UI
        setDefaultCard(null);
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Failed to fetch balance:', e);
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('wallet:refresh', onFocus);
    if (window.location.pathname.startsWith('/billing/')) refresh();
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('wallet:refresh', onFocus);
    };
  }, []);

  useEffect(() => {
    const seed = readInsufficientFlag();
    if (seed) {
      setShowInsufficient(true);
      setInsufficientMeta(seed);
    }
    const onSet = () => {
      const f = readInsufficientFlag();
      setShowInsufficient(Boolean(f));
      setInsufficientMeta(f || null);
    };
    const onClear = () => {
      setShowInsufficient(false);
      setInsufficientMeta(null);
    };
    const onTopUp = () => {
      clearInsufficientFlag();
      setShowInsufficient(false);
      setInsufficientMeta(null);
    };
    window.addEventListener('wallet:insufficient', onSet);
    window.addEventListener('wallet:insufficient.cleared', onClear);
    window.addEventListener('wallet:toppedup', onTopUp);
    return () => {
      window.removeEventListener('wallet:insufficient', onSet);
      window.removeEventListener('wallet:insufficient.cleared', onClear);
      window.removeEventListener('wallet:toppedup', onTopUp);
    };
  }, []);

  // Auto-clear the banner once there is a positive balance again
  useEffect(() => {
    if (!showInsufficient) return;
    const required = Number(insufficientMeta?.required);
    if (
      Number.isFinite(required) &&
      typeof balance === 'number' &&
      balance >= required
    ) {
      clearInsufficientFlag();
      setShowInsufficient(false);
      setInsufficientMeta(null);
    }
  }, [balance, showInsufficient, insufficientMeta]);

  async function handleBuy() {
    // First-time (or cardless) users: open the payment flyout instead of
    // redirecting to Checkout. They add a card once, then future Buys are instant.
    if (stripeEnabled && !defaultCard) {
      setShowBillingFlyout(true);
      return;
    }

    try {
      setBuying(true);

      // If we have a card summary in this session, try instant purchase first
      if (defaultCard) {
        try {
          const resp = await creditService.instantPurchase(packCode, null, customAmount);
          if (resp && typeof resp.balance === 'number') {
            setBalance(resp.balance);
          } else {
            // Fallback: refresh balance from server if response is unexpected
            await refresh();
          }

          clearInsufficientFlag();
          window.dispatchEvent(new Event('wallet:toppedup'));
          return; // success, no redirect
        } catch (e) {
          const code = e?.code || e?.body?.error;

          // No saved card or Stripe not fully configured – fall back to Checkout
          if (code === 'no_saved_card' || code === 'stripe_not_configured') {
            if (process.env.NODE_ENV !== 'production') {
              console.warn(
                'Instant purchase unavailable, falling back to Checkout',
                e
              );
            }
            // fall through to Checkout logic below
          } else if (code === 'card_error') {
            // Card was declined or requires action
            const msg =
              e?.body?.message ||
              e?.message ||
              'Your card could not be charged. Please try a different card.';
            alert(msg);
            return; // don't auto-fallback; user needs to fix card
          } else if (code === 'payment_not_succeeded') {
            // PaymentIntent did not reach succeeded state (e.g. requires_action)
            if (process.env.NODE_ENV !== 'production') {
              console.warn(
                'Instant purchase PaymentIntent not succeeded, falling back',
                e
              );
            }
            // fall through to Checkout
          } else {
            // Unknown error; log and fall back
            if (process.env.NODE_ENV !== 'production') {
              console.warn(
                'Instant purchase failed, falling back to Checkout',
                e
              );
            }
            // fall through to Checkout
          }
        }
      }

      // Legacy Stripe Checkout flow (redirect)
      const { url } = await creditService.createCheckout(packCode, null, customAmount);
      if (url && url.includes('/billing/mock')) {
        // mock: stay on page and just refresh
        await refresh();
        clearInsufficientFlag();
        window.dispatchEvent(new Event('wallet:toppedup'));
        return;
      }
      if (url) {
        window.location.href = url;
      }
    } catch (e) {
      alert(e?.body?.error || e?.message || 'Checkout failed.');
    } finally {
      setBuying(false);
    }
  }

  async function handlePortal() {
    try {
      const { url } = await creditService.openBillingPortal();
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      alert(e?.body?.error || e?.message || 'Billing portal unavailable.');
      throw e;
    }
  }

  return (
    <Fragment>
      {/* Separator line matching playground dividers */}
      <div className="wallet-separator" />

      {/* Wallet card styled to match playground blocks */}
      <div className={`playground-block wallet-block ${className}`}>
        <div className="playground-header wallet-header">
          <div className="wallet-title-group">
            <span>Wallet</span>
            <button
              className="wallet-info-icon"
              onClick={() => setShowPricingInfo(true)}
              aria-label="View pricing"
              title="Pricing & Costs"
            >
              ℹ
            </button>
          </div>
          <button
            className="playground-plus wallet-gear"
            onClick={() => setShowBillingFlyout(true)}
            aria-label="Manage Payment Method"
            title="Manage Payment Method"
          >
            ⚙
          </button>
        </div>

        <div className="wallet-body">
          <div className="wallet-row">
            <div className="wallet-label artifact-empty">Credit Balance</div>
            <div className={`wallet-value artifact-empty ${(balance ?? 0) < 0 ? 'wallet-value-negative' : ''}`}>
              {isLoading ? '…' : balance ?? 0}
            </div>
          </div>

          {defaultCard && (
            <div className="wallet-row">
              <div className="wallet-label artifact-empty">Payment Method</div>
              <div className="wallet-value artifact-empty">
                {(defaultCard.brand || 'Card').toUpperCase()} ending in{' '}
                {defaultCard.last4}{' '}
                {defaultCard.expMonth && defaultCard.expYear && (
                  <span className="wallet-exp-date">
                    — exp {defaultCard.expMonth}/
                    {String(defaultCard.expYear || '').slice(-2)}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="wallet-row">
            <div className="wallet-label artifact-empty">Add Credits</div>
            <div className="wallet-control">
              {/* Custom Editable Dropdown (ComboBox) */}
              <div
                className="buy-combobox buy-scale"
                ref={dropdownRef}
                title="1 credit ≈ $0.10"
              >
                <input
                  type="number"
                  className="buy-input"
                  value={customAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCustomAmount(val);
                    setPackCode('custom');
                    setShowDropdown(false);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  aria-label="Enter credit amount"
                />

                <button
                  className="buy-chevron"
                  onClick={() => setShowDropdown(!showDropdown)}
                  tabIndex={-1}
                >
                  ▼
                </button>

                {showDropdown && (
                  <div className="buy-dropdown">
                    {[
                      { val: '100', label: '100' },
                      { val: '250', label: '250' },
                      { val: '500', label: '500' },
                      { val: '1000', label: '1000' },
                    ].map((opt) => (
                      <div
                        key={opt.val}
                        className="buy-dropdown-item"
                        onClick={() => {
                          setCustomAmount(opt.val);
                          setPackCode(`credits_${opt.val}`);
                          setShowDropdown(false);
                        }}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                className="buy-btn buy-scale"
                onClick={handleBuy}
                disabled={buying || !customAmount || Number(customAmount) < 10}
                title="1 credit ≈ $0.10"
              >
                {buying ? 'Buying…' : `Buy $${parseFloat((Number(customAmount || 0) / 10).toFixed(1))}`}
              </button>
            </div>
          </div>

          {/* First-time hint: no card yet or no purchase, explain what will happen */}
          {!hasMadePurchase && stripeEnabled && (
            <div className="wallet-first-time-hint" style={{ color: '#4ade80' }}>
              Special Offer! We'll match your first purchase! Get up to 1,000 extra credits on us.
            </div>
          )}
        </div>

        {showInsufficient && (
          <div className="credits-banner-insufficient">
            <strong style={{ color: '#fca5a5' }}>Insufficient credits.</strong>{' '}
            {insufficientMeta?.required != null &&
              insufficientMeta?.balance != null
              ? `Need ${insufficientMeta.required}, you have ${insufficientMeta.balance}.`
              : `Please add credits to continue.`}
          </div>
        )}
      </div>

      <PricingFlyout
        open={showPricingInfo}
        onClose={() => setShowPricingInfo(false)}
      />

      {/* Stripe Elements wrapper just around the flyout */}
      <Elements stripe={stripePromise}>
        <PaymentMethodFlyout
          open={showBillingFlyout}
          onClose={() => setShowBillingFlyout(false)}
          onSaved={(summary) => setDefaultCard(summary)}
          onOpenPortal={handlePortal}
        />
      </Elements>
    </Fragment >
  );
}
